import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { logger } from '@/lib/logger';
import { persistentLogger } from '@/lib/persistent-logger';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const correlationId = logger.getCorrelationId();
    
    // ✅ ADICIONAR AO PERSISTENT LOGGER
    persistentLogger.addLog('APP', 'fatal', 'React ErrorBoundary caught error', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href
    });
    
    // Use structured logger
    logger.error('ErrorBoundary caught error', error, {
      component: 'ErrorBoundary',
      componentStack: errorInfo.componentStack,
      correlationId,
    });
    
    // Enviar para backend para agregação
    import('@/integrations/supabase/client').then(({ supabase }) => {
      supabase.functions.invoke('log-frontend-error', {
        body: {
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
          },
          correlationId,
          componentStack: errorInfo.componentStack,
          userAgent: navigator.userAgent,
          url: window.location.href,
        }
      }).catch(() => {}); // Silent fail
    });
    
    // Capturar no Sentry se disponível
    const globalWindow = window as any;
    if (globalWindow.Sentry) {
      Sentry.captureException(error, { 
        contexts: { 
          react: { 
            componentStack: errorInfo.componentStack 
          } 
        } 
      });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <div className="text-center max-w-md space-y-6">
            <div className="space-y-2">
              <div className="text-6xl mb-4">⚠️</div>
              <h1 className="text-2xl font-bold text-foreground">Algo deu errado</h1>
              <p className="text-muted-foreground">
                Desculpe, encontramos um erro inesperado. Nossa equipe foi notificada.
              </p>
            </div>
            
            {this.state.error && import.meta.env.DEV && (
              <div className="text-left p-4 bg-muted rounded-lg overflow-auto max-h-48">
                <code className="text-xs text-destructive">
                  {this.state.error.toString()}
                </code>
              </div>
            )}
            
            <div className="space-y-3">
              <Button 
                onClick={this.handleReset}
                className="w-full"
                size="lg"
              >
                Voltar ao Início
              </Button>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full"
                size="lg"
              >
                Recarregar Página
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
